import React, { useMemo } from "react";
import { Box, Button, Sheet, Typography } from "@mui/joy";
import { useAccumulatedViewRows } from "../../../hooks/useAccumulatedViewRows";
import type { CanvasDataSource } from "../../../types";
import type { RenderableElement } from "../CanvasRenderer";
import { useCanvasRuntime } from "../CanvasRuntimeContext";
import { sanitizeStatusColors } from "../canvasStyleOverrides";
import StatusChip from "./StatusChip";
import CanvasStaticStepper, { readStaticStepper } from "./CanvasStaticStepper";

export interface TimelineConfig {
  widget_type: "TIMELINE";
  data_source_alias: string;
  title?: string;
  timeline_config?: Pick<
    TimelineConfig,
    | "label_field"
    | "status_field"
    | "date_field"
    | "description_field"
    | "order_field"
  >;
  label_field: string;
  status_field?: string;
  date_field?: string;
  description_field?: string;
  order_field?: string;
}

interface Props {
  element: RenderableElement;
  dataSources: CanvasDataSource[];
  canvasId?: string | null;
}

const display = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

export function CanvasTimelineRenderer(props: Props) {
  const stepper = readStaticStepper(props.element.props);
  if (stepper) {
    return (
      <CanvasStaticStepper
        config={stepper}
        title={
          typeof props.element.props.title === "string"
            ? props.element.props.title
            : undefined
        }
      />
    );
  }
  return <DataTimeline {...props} />;
}

function DataTimeline({ element, dataSources, canvasId }: Props) {
  const outer = element.props as unknown as TimelineConfig;
  const config = { ...outer, ...outer.timeline_config };
  const statusColors = sanitizeStatusColors(
    (config as { status_colors?: unknown; statusColors?: unknown })
      .status_colors ?? (config as { statusColors?: unknown }).statusColors,
  );
  const runtime = useCanvasRuntime();
  const effectiveCanvasId = canvasId ?? runtime?.canvasId ?? null;
  const alias = config.data_source_alias || "main";
  const source = dataSources.find((candidate) =>
    [
      candidate.id,
      candidate.name,
      (candidate as { alias?: string }).alias,
    ].includes(alias),
  );
  const viewId = source?.view_id ? String(source.view_id) : null;
  const relationshipFilters = useMemo(
    () =>
      runtime?.interactionBus
        .getRuntimeFilters({
          canvasId: effectiveCanvasId,
          elementId: element.id,
          dataSourceAlias: alias,
        })
        .flatMap((entry) => entry.filters) || [],
    [alias, effectiveCanvasId, element.id, runtime],
  );
  const {
    rows: loadedRows,
    totalCount,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
  } = useAccumulatedViewRows(viewId, {
    canvasId: effectiveCanvasId || undefined,
    filters: relationshipFilters.length ? relationshipFilters : undefined,
    filterLogic: "AND",
    sort: config.order_field
      ? [{ field: config.order_field, direction: "ASC" }]
      : undefined,
  });

  const rows = useMemo(() => {
    const input = loadedRows;
    if (!config.order_field) return input;
    return [...input].sort((a, b) => {
      const left = a[config.order_field!];
      const right = b[config.order_field!];
      return typeof left === "number" && typeof right === "number"
        ? left - right
        : display(left).localeCompare(display(right));
    });
  }, [config.order_field, loadedRows]);

  return (
    <Sheet
      variant="outlined"
      sx={{ p: 2, borderRadius: "sm", height: "100%", overflowY: "auto" }}
    >
      {config.title && (
        <Typography level="title-md" sx={{ mb: 2 }}>
          {config.title}
        </Typography>
      )}
      {!effectiveCanvasId || !viewId || !config.label_field ? (
        <Typography level="body-sm">
          Configure a view and label field for this timeline.
        </Typography>
      ) : error ? (
        <Typography role="alert" level="body-sm">
          Unable to load timeline.
        </Typography>
      ) : isLoading ? (
        <Typography level="body-sm">Loading timeline…</Typography>
      ) : rows.length === 0 ? (
        <Typography level="body-sm">No timeline events yet.</Typography>
      ) : (
        <Box
          component="ol"
          aria-label={config.title || "Timeline"}
          sx={{ m: 0, pl: 3 }}
        >
          {rows.map((row, index) => (
            <Box component="li" key={row._rowId || index} sx={{ pb: 2, pl: 1 }}>
              <Typography level="title-sm">
                {display(row[config.label_field])}
              </Typography>
              {config.status_field && display(row[config.status_field]) && (
                <Box component="span" sx={{ mr: 1 }}>
                  <StatusChip
                    value={display(row[config.status_field])}
                    statusColors={statusColors}
                  />
                </Box>
              )}
              {config.date_field && display(row[config.date_field]) && (
                <Typography level="body-xs" component="span">
                  {display(row[config.date_field])}
                </Typography>
              )}
              {config.description_field &&
                display(row[config.description_field]) && (
                  <Typography level="body-sm">
                    {display(row[config.description_field])}
                  </Typography>
                )}
            </Box>
          ))}
        </Box>
      )}
      {hasMore && totalCount !== undefined && rows.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <Typography level="body-sm">
            Showing {rows.length} of {totalCount} events
          </Typography>
          <Button
            size="sm"
            variant="outlined"
            loading={isLoadingMore}
            onClick={loadMore}
          >
            Load more
          </Button>
        </Box>
      )}
    </Sheet>
  );
}
